// Access-control context — step 3 of the Portal access-control migration
// (see jli-portal-be/docs/ACCESS_CONTROL_PLAN.md §8). Wraps GET
// /access-control/my: the BE's resolved per-position/per-user page grants
// for the signed-in session. While the system is in "legacy" mode the BE
// computes these grants from today's App Modules map + rank, so behaviour
// is unchanged until the matrix is activated (§9) — this context is a
// drop-in replacement for the ad-hoc client-side checks that used to live
// in ProtectedRoute and the nav, not a new gate.
//
// Mounted ONCE at the app root (see the export note below) so every page
// shares one fetch and one 15s-throttled refresh cadence, rather than each
// ProtectedRoute re-fetching on its own.
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
    ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import { apiFetch } from "../api/apiFetch";

export type AccessMode = "legacy" | "shadow" | "active";
export type AccessLevel = "none" | "read" | "write";
export type AccessPages = Record<string, "read" | "write">;

export interface AccessContextValue {
    mode: AccessMode;
    /** Raw key -> level map, exactly as returned by the BE. A key absent
     *  here means "none" — the BE never sends explicit "none" entries. */
    pages: AccessPages;
    /** True for "read" or "write" on this key. */
    hasAccess: (key: string) => boolean;
    /** True only for "write" on this key. */
    canWrite: (key: string) => boolean;
    /** "none" | "read" | "write" for this key (never throws on an unknown key). */
    level: (key: string) => AccessLevel;
    /** A fetch is currently in flight (initial load or a refresh). */
    loading: boolean;
    /** The last fetch attempt failed (network / non-2xx). `pages` still
     *  holds whatever was last loaded successfully, if anything — callers
     *  must not treat a stale `pages` as authoritative while `failed`. */
    failed: boolean;
    /** False only until the FIRST fetch attempt (success or failure) has
     *  settled, or immediately when nobody is signed in. Consumers must
     *  never decide "no access" while this is false — that's the
     *  first-frame trap that has bitten the module-visibility guard before. */
    ready: boolean;
    /** Force an immediate re-fetch, bypassing the route-change throttle. */
    refresh: () => void;
    /** True inside a real <AccessProvider>; false for the permissive stub
     *  returned outside one (unit tests, isolated renders). */
    provided: boolean;
    /** The context may DECIDE visibility: a real provider, first fetch settled,
     *  not failed. Nav / Home use this to pick between the access map and the
     *  pre-access-control rank rules — the stub must never win that choice,
     *  or every isolated render would show the Admin side. */
    authoritative: boolean;
    /** The last refresh failed but `pages` still holds a map fetched earlier IN
     *  THIS SESSION. Guards keep deciding from that map rather than dropping to
     *  the rank rules — a network blip must not read as a revocation (review
     *  2026-09-24 AC-07). False on a cold start with nothing loaded yet. */
    stale: boolean;
}

const isLoggedIn = (): boolean => {
    try {
        const v = window.localStorage.getItem("isLoggedIn");
        return v !== null && v !== "false";
    } catch {
        return false;
    }
};

// Outside the provider (most unit tests, and any page rendered in
// isolation without the app shell) access is permissive — nothing here
// should ever start denying pages that were never wired to a real guard.
// This mirrors how `useModuleVisibility(undefined)` behaves today.
const STUB_VALUE: AccessContextValue = {
    mode: "legacy",
    pages: {},
    hasAccess: () => true,
    canWrite: () => true,
    level: () => "write",
    loading: false,
    failed: false,
    ready: true,
    refresh: () => {},
    provided: false,
    authoritative: false,
    stale: false,
};

const AccessContext = createContext<AccessContextValue | null>(null);

// A page can navigate several times in a few seconds (hub -> module ->
// back); refetching /access-control/my on every one of those is wasted
// traffic for data that only changes when an admin edits a rule. 15s
// mirrors the plan's "next navigation" revocation guidance (§7.5) without
// hammering the endpoint.
const ROUTE_CHANGE_THROTTLE_MS = 15000;

export const AccessProvider = ({ children }: { children: ReactNode }) => {
    const location = useLocation();
    const [pages, setPages] = useState<AccessPages>({});
    const [mode, setMode] = useState<AccessMode>("legacy");
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);
    const [ready, setReady] = useState(false);
    // A map was loaded successfully in the current session (cleared on logout).
    const [hadGoodMap, setHadGoodMap] = useState(false);

    const lastFetchAtRef = useRef(0);
    const hasFetchedOnceRef = useRef(false);
    // Session generation (review 2026-09-24 AC-08): bumped on every logout /
    // signed-out reset. A fetch remembers the generation it started under and
    // its response is DROPPED if the generation moved on — so user A's slow
    // /access-control/my can never populate user B's map on a shared scanner.
    // In-flight tracking is per generation for the same reason: B's fetch must
    // not be skipped because A's is still pending.
    const sessionGenRef = useRef(0);
    const inFlightGenRef = useRef<number | null>(null);
    const mountedRef = useRef(true);
    useEffect(() => () => { mountedRef.current = false; }, []);

    const fetchAccess = useCallback((force: boolean) => {
        if (!isLoggedIn()) {
            // Nothing to fetch. Reset to the empty/ready state so a route
            // guard evaluated on the login page (or right after logout)
            // never sits in "loading" forever.
            sessionGenRef.current += 1;
            hasFetchedOnceRef.current = false;
            lastFetchAtRef.current = 0;
            setPages({});
            setMode("legacy");
            setLoading(false);
            setFailed(false);
            setHadGoodMap(false);
            setReady(true);
            return;
        }

        const now = Date.now();
        if (!force && now - lastFetchAtRef.current < ROUTE_CHANGE_THROTTLE_MS) return;
        const gen = sessionGenRef.current;
        if (inFlightGenRef.current === gen) return;

        inFlightGenRef.current = gen;
        lastFetchAtRef.current = now;
        setLoading(true);
        // First fetch since login: the provider may have been "ready" with an
        // EMPTY map from the logged-out state (mounted before login, SPA
        // navigation). Guards must wait for this answer, not deny against
        // that empty map — that is exactly what bounced an Admin off Put
        // Away with "no access" on the first click after logging in.
        if (!hasFetchedOnceRef.current) setReady(false);

        const live = () => mountedRef.current && sessionGenRef.current === gen;
        apiFetch("/access-control/my")
            .then(async (res) => {
                if (!res.ok) {
                    if (live()) setFailed(true);
                    return;
                }
                const data = await res.json().catch(() => null);
                if (!data || typeof data !== "object") {
                    if (live()) setFailed(true);
                    return;
                }
                if (!live()) return; // answer for a session that has since ended — drop it
                setPages(
                    data.pages && typeof data.pages === "object"
                        ? (data.pages as AccessPages)
                        : {},
                );
                setMode(data.mode === "shadow" || data.mode === "active" ? data.mode : "legacy");
                setFailed(false);
                setHadGoodMap(true);
            })
            .catch(() => {
                // Network failure — deliberately leave `pages` untouched so a
                // transient blip doesn't wipe out access the user already had.
                if (live()) setFailed(true);
            })
            .finally(() => {
                if (sessionGenRef.current !== gen) return; // the next session owns the flags now
                inFlightGenRef.current = null;
                hasFetchedOnceRef.current = true;
                if (mountedRef.current) {
                    setLoading(false);
                    setReady(true);
                }
            });
    }, []);

    // Initial fetch on mount (and whenever the login flag flips true, e.g.
    // a fresh sign-in without a full page reload — App.tsx unmounts the
    // pre-login tree so this remounts the provider).
    useEffect(() => {
        fetchAccess(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Refetch on route change, throttled.
    //
    // Before the first fetch has settled there are two very different cases:
    //   - a fetch is in flight (the mount effect fired while signed in) —
    //     fetchAccess() dedupes via inFlightRef, so calling it again is a no-op;
    //   - the provider settled in the SIGNED-OUT state (mounted on /login,
    //     which is the normal path — the provider wraps the whole app and is
    //     never remounted) and the operator has since signed in. login.tsx
    //     navigates inside the SPA, so this route change is the ONLY signal
    //     that a fetch is now due. Skipping it left an empty-but-ready map,
    //     and every guarded route bounced Home with "no access to that
    //     module" until a hard reload (Red/Admin, 2026-09-18).
    // Forced, because lastFetchAtRef was zeroed by the signed-out reset and
    // the throttle must never stand between a fresh sign-in and its grants.
    useEffect(() => {
        if (!hasFetchedOnceRef.current) {
            if (isLoggedIn()) fetchAccess(true);
            return;
        }
        fetchAccess(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    // A 403 no_access/read_only means our cached pages just proved wrong —
    // bypass the throttle and refetch immediately (apiFetch dispatches this).
    useEffect(() => {
        const onDenied = () => fetchAccess(true);
        window.addEventListener("portal:access-denied", onDenied);
        return () => window.removeEventListener("portal:access-denied", onDenied);
    }, [fetchAccess]);

    // Clear on logout. `portal:logout` fires in the tab that called
    // logoutProcessor(); the `storage` event fires in every OTHER tab once
    // `isLoggedIn` flips in localStorage (this tab's own writes don't fire
    // its own storage event, hence the two listeners).
    useEffect(() => {
        const clear = () => {
            sessionGenRef.current += 1;
            inFlightGenRef.current = null;
            hasFetchedOnceRef.current = false;
            lastFetchAtRef.current = 0;
            setPages({});
            setMode("legacy");
            setLoading(false);
            setFailed(false);
            setHadGoodMap(false);
            setReady(true);
        };
        const onStorage = (e: StorageEvent) => {
            if (e.key === "isLoggedIn" && (e.newValue === null || e.newValue === "false")) clear();
        };
        window.addEventListener("portal:logout", clear);
        window.addEventListener("storage", onStorage);
        return () => {
            window.removeEventListener("portal:logout", clear);
            window.removeEventListener("storage", onStorage);
        };
    }, []);

    const hasAccess = useCallback((key: string) => !!pages[key], [pages]);
    const canWrite = useCallback((key: string) => pages[key] === "write", [pages]);
    const level = useCallback((key: string): AccessLevel => pages[key] ?? "none", [pages]);
    const refresh = useCallback(() => fetchAccess(true), [fetchAccess]);

    // `ready` must be false on the very first render after a sign-in, not just
    // after the fetch effect has had a chance to run. Effects run AFTER render:
    // when login.tsx navigates straight back to a guarded page, that page's
    // ProtectedRoute renders once with the signed-out state (ready: true,
    // pages: {}) before the route-change effect above can start the fetch —
    // and denies. Deriving readiness at render time closes that gap: signed in
    // but never fetched means "wait", whatever the state flag says.
    const effectiveReady = ready && (hasFetchedOnceRef.current || !isLoggedIn());

    const stale = failed && hadGoodMap;
    const value: AccessContextValue = {
        mode, pages, hasAccess, canWrite, level, loading, failed, ready: effectiveReady, refresh,
        provided: true,
        // A stale-but-real map still decides (AC-07); only a cold failure falls back.
        authoritative: effectiveReady && (!failed || stale),
        stale,
    };

    return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
};

/** Reads the access-control context. Outside an <AccessProvider> (most unit
 *  tests, isolated component renders) this returns a permissive stub so
 *  nothing that doesn't explicitly test access control starts failing. */
export const useAccess = (): AccessContextValue => {
    const ctx = useContext(AccessContext);
    return ctx ?? STUB_VALUE;
};
